'use client'

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api'
import { getWeekKey } from '@/lib/dates'
import Badge, { CatBadge } from '@/components/ui/Badge'
import toast from 'react-hot-toast'
import type { Task, PlanItem } from '@/types'
import { ChevronLeft, ChevronRight, RotateCcw, Plus, X, GripVertical } from 'lucide-react'

export default function PlanningPage() {
  const { isLoading, tasks, activeProjectIds, activeEngineers } = useDashboardData()
  const [weekOffset, setWeekOffset] = useState(0)
  const [planData, setPlanData] = useState<PlanItem[]>([])
  const [prevData, setPrevData] = useState<PlanItem[]>([])
  const [search, setSearch] = useState('')
  const [loadingPlan, setLoadingPlan] = useState(false)

  const weekKey = useMemo(() => getWeekKey(weekOffset), [weekOffset])
  const prevWeekKey = useMemo(() => getWeekKey(weekOffset - 1), [weekOffset])

  const loadPlan = useCallback(async () => {
    setLoadingPlan(true)
    const [cur, prev] = await Promise.all([
      apiGet<PlanItem[]>(`/api/planning/${weekKey}`),
      apiGet<PlanItem[]>(`/api/planning/${prevWeekKey}`),
    ])
    setPlanData(cur || [])
    setPrevData(prev || [])
    setLoadingPlan(false)
  }, [weekKey, prevWeekKey])

  useEffect(() => { loadPlan() }, [loadPlan])

  // Backlog: active tasks not yet planned
  const backlog = useMemo(() => {
    const plannedIds = new Set(planData.map(p => p.task_id))
    const q = search.toLowerCase()
    return tasks.filter(t =>
      !t.cancelled && activeProjectIds.includes(t.projId) &&
      t.stage !== 'Done' && t.stage !== 'Cancelled' &&
      !plannedIds.has(t.id) &&
      (!q || t.name.toLowerCase().includes(q) || t.proj.toLowerCase().includes(q) || t.users.join(' ').toLowerCase().includes(q))
    ).sort((a, b) => (b.age || 0) - (a.age || 0)).slice(0, 100)
  }, [tasks, planData, search, activeProjectIds])

  // Plan summary
  const summary = useMemo(() => {
    const doneTasks = planData.filter(p => tasks.find(t => t.id === p.task_id)?.stage === 'Done')
    return {
      planned: planData.length,
      totalHours: planData.reduce((a, p) => a + p.planned_hours, 0),
      done: doneTasks.length,
      rate: planData.length > 0 ? (doneTasks.length / planData.length) * 100 : 0,
    }
  }, [planData, tasks])

  // Previous week review
  const prevReview = useMemo(() => {
    const completed = prevData.filter(p => tasks.find(t => t.id === p.task_id)?.stage === 'Done' || p.status === 'completed')
    const missed = prevData.filter(p => {
      const t = tasks.find(t => t.id === p.task_id)
      return t && t.stage !== 'Done' && p.status !== 'completed'
    })
    return {
      total: prevData.length,
      completed: completed.length,
      missed,
      rate: prevData.length > 0 ? (completed.length / prevData.length) * 100 : 0,
    }
  }, [prevData, tasks])

  async function addToPlan(task: Task) {
    const res = await apiPost<{ ok: boolean; id: number }>('/api/planning/', {
      week_key: weekKey,
      task_id: task.id,
      task_name: task.name,
      project_name: task.proj,
      category: task.cat,
      assigned_to: task.users.join(', '),
      planned_hours: task.alloc_h || 0,
    })
    if (res?.ok) {
      toast.success('Added to plan')
      loadPlan()
    }
  }

  async function removeFromPlan(itemId: number) {
    await apiDelete(`/api/planning/${itemId}`)
    loadPlan()
  }

  async function updateHours(itemId: number, hours: number) {
    await apiPut(`/api/planning/${itemId}`, { planned_hours: hours })
  }

  async function carryOver() {
    const res = await apiPost<{ ok: boolean; carried_over: number; from_week: string }>('/api/planning/carry-over')
    if (res?.ok) {
      toast.success(`${res.carried_over} tasks carried over from ${res.from_week}`)
      loadPlan()
    }
  }

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold">
            Weekly Planning <span className="text-sm font-normal text-slate-400 ml-2">{weekKey}</span>
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setWeekOffset(o => o - 1)} className="p-1.5 text-slate-400 hover:text-slate-600 border border-slate-200 dark:border-slate-700"><ChevronLeft size={14} /></button>
            <button onClick={() => setWeekOffset(0)} className="px-3 py-1 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 dark:border-slate-700">This Week</button>
            <button onClick={() => setWeekOffset(o => o + 1)} className="p-1.5 text-slate-400 hover:text-slate-600 border border-slate-200 dark:border-slate-700"><ChevronRight size={14} /></button>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
            <button onClick={carryOver} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700">
              <RotateCcw size={12} /> Carry Over Incomplete
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { value: summary.planned, label: 'Tasks Planned', color: 'text-slate-900 dark:text-slate-100' },
            { value: `${summary.totalHours.toFixed(0)}h`, label: 'Hours Planned', color: 'text-slate-900 dark:text-slate-100' },
            { value: summary.done, label: 'Completed', color: 'text-slate-900 dark:text-slate-100' },
            { value: `${summary.rate.toFixed(0)}%`, label: 'Completion Rate', color: summary.rate >= 80 ? 'text-emerald-700 dark:text-emerald-400' : summary.rate >= 50 ? 'text-amber-700 dark:text-amber-400' : 'text-red-700 dark:text-red-400' },
          ].map(s => (
            <div key={s.label} className="bg-slate-50 dark:bg-slate-800/50 p-3 text-center">
              <div className={`text-xl font-bold font-[family-name:var(--font-display)] ${s.color}`}>{s.value}</div>
              <div className="text-[0.65rem] uppercase tracking-wider text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Two columns: Backlog + Planned */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Backlog */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">Backlog</span>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tasks..."
                className="flex-1 px-2.5 py-1 text-sm bg-transparent border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 focus:outline-none focus:border-blue-500 placeholder:text-slate-400"
              />
            </div>
            <div className="border border-slate-200 dark:border-slate-800 max-h-[420px] overflow-y-auto">
              {backlog.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">No matching tasks</div>
              ) : backlog.map(t => (
                <div key={t.id} draggable onDragStart={e => e.dataTransfer.setData('text/plain', String(t.id))}
                  className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 text-sm group cursor-grab active:cursor-grabbing">
                  <CatBadge cat={t.cat} />
                  <span className="flex-1 truncate" title={t.name}>{t.name}</span>
                  <span className="text-sm text-slate-400 truncate max-w-[80px]">{t.users.join(', ').split(' ')[0]}</span>
                  <span className="text-sm text-slate-400">{t.alloc_h ? `${t.alloc_h}h` : '—'}</span>
                  <button onClick={() => addToPlan(t)} className="opacity-0 group-hover:opacity-100 p-0.5 text-blue-500 hover:text-blue-700 transition-opacity" title="Add to plan">
                    <Plus size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Planned */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold uppercase tracking-wider text-slate-400 font-[family-name:var(--font-display)]">Planned for {weekKey}</span>
            </div>
            <div className="border border-slate-200 dark:border-slate-800 min-h-[200px] transition-colors"
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-blue-400') }}
              onDragLeave={e => e.currentTarget.classList.remove('border-blue-400')}
              onDrop={e => {
                e.preventDefault()
                e.currentTarget.classList.remove('border-blue-400')
                const taskId = parseInt(e.dataTransfer.getData('text/plain'))
                const task = tasks.find(t => t.id === taskId)
                if (task) addToPlan(task)
              }}>
              {loadingPlan ? (
                <div className="p-6 text-center text-sm text-slate-400">Loading...</div>
              ) : planData.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">Drag tasks here or click +</div>
              ) : planData.map(p => {
                const isDone = tasks.find(t => t.id === p.task_id)?.stage === 'Done'
                return (
                  <div key={p.id} className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800/50 text-sm ${isDone ? 'opacity-50' : ''}`}>
                    <GripVertical size={12} className="text-slate-300" />
                    <CatBadge cat={p.category} />
                    <span className="flex-1 truncate" title={p.task_name}>{p.task_name}</span>
                    <span className="text-sm text-slate-400 truncate max-w-[60px]">{p.assigned_to.split(',')[0]}</span>
                    <input
                      type="number"
                      defaultValue={p.planned_hours}
                      min={0}
                      step={0.5}
                      onBlur={e => updateHours(p.id, parseFloat(e.target.value) || 0)}
                      className="w-14 px-1.5 py-0.5 text-center text-sm border border-slate-200 dark:border-slate-700 bg-transparent focus:outline-none focus:border-blue-500"
                      title="Planned hours"
                    />
                    {isDone ? <Badge variant="success">Done</Badge> : <Badge variant={p.status === 'carried_over' ? 'warning' : 'default'}>{p.status}</Badge>}
                    <button onClick={() => removeFromPlan(p.id)} className="p-0.5 text-slate-300 hover:text-red-500" title="Remove">
                      <X size={14} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Previous Week Review */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-3">Previous Week Review <span className="text-sm font-normal text-slate-400 ml-1">{prevWeekKey}</span></h3>
        {prevData.length === 0 ? (
          <p className="text-sm text-slate-400">No plan data for previous week.</p>
        ) : (
          <>
            <div className="flex gap-4 mb-4 text-sm">
              <span>Planned: <strong>{prevReview.total}</strong></span>
              <span className="text-emerald-600">Completed: <strong>{prevReview.completed}</strong></span>
              <span className="text-red-600">Missed: <strong>{prevReview.missed.length}</strong></span>
              <span>Rate: <strong className={prevReview.rate >= 80 ? 'text-emerald-600' : prevReview.rate >= 50 ? 'text-amber-600' : 'text-red-600'}>{prevReview.rate.toFixed(0)}%</strong></span>
            </div>
            {prevReview.missed.length > 0 && (
              <div>
                <div className="text-sm uppercase tracking-wider text-slate-400 mb-2">Missed items</div>
                {prevReview.missed.map(p => (
                  <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 dark:border-slate-800/50 text-sm text-slate-500">
                    <span className="flex-1 truncate">{p.task_name}</span>
                    <span className="text-sm">{p.assigned_to}</span>
                    <span className="text-sm">{p.planned_hours}h</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
